import * as React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Details from "Catalog/Edit/shared/components/Details"
import EditLayout from "Catalog/Edit/shared/components/EditLayout"
import useDelayedRenderedItem from "Catalog/Edit/shared/hooks/useDelayedRenderedItem"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { CategoryId, ProductId } from "shared/hubrise/ids"
import { ICategoryOrProduct, isCategory, isCategory as utilIsCategory } from "shared/hubrise/models"
import { Category, Product } from "shared/hubrise/utils"
import { sortedCategoriesAndProducts } from "shared/hubrise/utils/Product"
import { tagOptions } from "shared/hubrise/utils/tags"

import Content from "./Content"
import Navigator from "./Navigator"
import Toolbar from "./Toolbar"

const Products = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ productId: string; categoryId: string }>()

  const { catalog } = useEditor()
  const items = React.useMemo(() => sortedCategoriesAndProducts(catalog), [catalog])

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.editPath("products")

  const selectedItem = useFindItemOrRedirect<ICategoryOrProduct>(
    params.productId ?? params.categoryId,
    (id) => {
      if (params.categoryId !== undefined) {
        return Category.byId(catalog, CategoryId(Number(id)))
      } else if (params.productId !== undefined) {
        return Product.byId(catalog, ProductId(Number(id)))
      }
    },
    indexPath,
  )

  const renderedItem = useDelayedRenderedItem(selectedItem)

  React.useEffect(() => {
    if (selectedItem && isCategory(selectedItem)) {
      visit("category", selectedItem)
    } else {
      visit("product", selectedItem)
    }
  }, [selectedItem, visit])

  useKeyboardNavigation<ICategoryOrProduct>({
    item: selectedItem,
    collection: items,
    indexPath: () => indexPath,
    showPath: (categoryOrProduct) =>
      utilIsCategory(categoryOrProduct)
        ? catalogNavigation.editPath("category", categoryOrProduct.id)
        : catalogNavigation.editPath("product", categoryOrProduct.id),
  })

  const productTagOptions = React.useMemo(() => tagOptions(t, catalog), [catalog, t])

  return (
    <EditLayout
      toolbar={<Toolbar categoryOrProduct={renderedItem} />}
      navigator={items.length > 0 ? <Navigator items={items} selectedId={selectedItem?.id} /> : null}
      details={
        renderedItem ? (
          <Details.Main testId="catalog-details" resourceId={renderedItem.id}>
            <Content categoryOrProduct={renderedItem} tagOptions={productTagOptions} />
          </Details.Main>
        ) : null
      }
      none={t("product.none")}
      noneSelected={t("product.none_selected")}
      testId="catalog-products"
    />
  )
}

export default Products
